import { DeepReadonly, IFinance } from 'types';
import { deepFreeze } from '../utils/deepFreeze.js';

export * from './restrictedCountryBanks.js';
export * from './currencies.js';

interface FundingSource {
  id: string;
  label: string;
  button: string;
  buttonAlt: string;
  saveButtonText: string;
}

export const defaultFinance: DeepReadonly<IFinance> = deepFreeze({
  courseFeeDepositReqd: '',
  courseFeeDeposit: undefined,
  courseFeeDepositDate: '',
  courseFeeDepositNotReqdReason: '',
  accomDepositReqd: '',
  accomDeposit: 0,
  accomDepositDate: '',
  organisationSponsorship: {},
  scholarshipSponsorship: {},
  educationSponsorship: {},
  familySponsorship: {},
  otherSponsorship: {},
  fundingSummary: {
    courseFees: [],
    studentLivingCost: {
      label: '',
      amount: 0,
      remainingAmount: 0,
      funding: [],
    },
    spouseLivingCost: {
      amount: 0,
      remainingAmount: 0,
      funding: [],
    },
    childrenLivingCost: {
      number: 0,
      amount: 0,
      remainingAmount: 0,
      funding: [],
    },
    totalToBeShown: 0,
    totalRemaining: 0,
    totalInvalid: 0,
  },
  isSectionStarted: false,
  isSectionComplete: false,
});

export const fundingSources: DeepReadonly<FundingSource[]> = deepFreeze([
  {
    id: 'organisationSponsorship',
    label: 'Company or Government Sponsorship',
    button: 'Add Sponsorship',
    buttonAlt: 'Add another Sponsorship',
    saveButtonText: 'Save Sponsorship Details',
  },
  {
    id: 'scholarshipSponsorship',
    label: 'University Scholarship or Discount',
    button: 'Add Scholarship',
    buttonAlt: 'Add another scholarship',
    saveButtonText: 'Save Scholarship Details',
  },
  {
    id: 'educationSponsorship',
    label: 'Education Loan',
    button: 'Add Loan',
    buttonAlt: 'Add another loan',
    saveButtonText: 'Save Loan Details',
  },
  {
    id: 'familySponsorship',
    label: 'Myself or My Family',
    button: 'Add Bank Account',
    buttonAlt: 'Add another bank account',
    saveButtonText: 'Save Funding Details',
  },
  {
    id: 'otherSponsorship',
    label: 'Other',
    button: 'Add Other',
    buttonAlt: 'Add another Other',
    saveButtonText: 'Save Other Details',
  },
]);

export const INNER_LONDON_LIVING_COST = 1529;
export const OUTER_LONDON_LIVING_COST = 1171;
export const MAX_ACCOMMODATION_DEPOSIT = 1529;

// DHA Subclass 500 — Australia. Annual figures, capped at the first 12 months
// of the course; travel is applied once. Source: Department of Home Affairs
// (May 2024 update, in force for 2026 intakes).
export const AU_LIVING_COST_ANNUAL = 29710; // primary applicant
export const AU_SPOUSE_DEPENDENT_ANNUAL = 10394; // spouse / de facto partner
export const AU_CHILD_DEPENDENT_ANNUAL = 4449; // per dependent child
export const AU_SCHOOL_AGE_CHILD_ANNUAL = 13502; // schooling costs, per school-age child

// Travel — applied once, not annualised. Charged from the student's home
// region; sub-region overrides on African countries pick out the higher tiers.
export const AU_TRAVEL_COST_DEFAULT = 2000;
export const AU_TRAVEL_COST_EAST_S_AFRICA = 2500;
export const AU_TRAVEL_COST_WEST_AFRICA = 3000;

// DHA only requires evidence covering the first 12 months of the course,
// regardless of the actual course length.
export const AU_FUNDS_DURATION_MONTHS_CAP = 12;
